import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  MediaFile,
  MEDIA_FILES_AI_VIDEO,
  MEDIA_FILES_ALL_VIDEO,
  MEDIA_FILES_IMAGE,
  MEDIA_FILES_VIDEO,
  getMediaFilterOptions,
} from '../../../data/models/LibraryFile/MediaFile';
import {isImageFile} from '../../../data/models/LibraryFile/VideoSourceShort';
import LibraryRepository from '../../../data/repositories/LibraryRepository';
import LoadingSpinner from '../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import SearchInput2 from '../../../uiToolkit/Inputs/SearchInput2/SearchInput2';
import FilterPill from '../../../uiToolkit/FilterPill';
import SortPill from '../../../uiToolkit/SortPill/SortPill';
import UploadVideoButton from '../../../uiToolkit/Inputs/UploadVideoButton';
import {FileUploadModalBody} from '../../../modules/FileUploadModalBody';
import {LIBRARY_SORT_OPTIONS} from '../../Library/conf';
import {SortOption} from '../../../data/models/UI/SelectOption';
import {appText} from '../../../assets/text/appText';
import {VideoEditorText} from '../VideoEditorText';
import EditMediaItemCard from './EditMediaItemCard';
import {MediaPickerResult} from './editMediaTypes';
import styles from './EditMediaBrowserPanel.module.css';

const PAGE_SIZE = 18;
const FILTER_ALL = -1;
const FILTER_IDS = [
  FILTER_ALL,
  MEDIA_FILES_VIDEO,
  MEDIA_FILES_IMAGE,
  MEDIA_FILES_AI_VIDEO,
];

interface Props {
  organizationId: string;
  pending: MediaPickerResult | null;
  onPick: (result: MediaPickerResult | null) => void;
  disabled?: boolean;
}

const fileToResult = (file: MediaFile): MediaPickerResult => {
  const isImage = isImageFile(file.fileType);
  const url = file.fileUrl || file.placeholderUrl || '';
  return {
    source: 'library',
    libraryMediaFileId: file.id,
    storyBlocksVideoId: null,
    thumbnailUrl: isImage ? url : file.placeholderUrl || url,
    previewUrl: url,
    fileDuration: file.duration || 0,
    kind: isImage ? 'image' : 'video',
  };
};

const isFilePickable = (file: MediaFile): boolean => {
  if (file.fileStatus !== 0) return false;
  const isImage = isImageFile(file.fileType);
  if (!isImage && (!file.duration || file.duration <= 0)) return false;
  return true;
};

const EditMediaLibrarySource = React.memo(
  ({organizationId, pending, onPick, disabled}: Props) => {
    const [files, setFiles] = useState<MediaFile[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [tmpSearch, setTmpSearch] = useState('');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [activeFilter, setActiveFilter] = useState(FILTER_ALL);
    const [uploadedFile, setUploadedFile] = useState<File | undefined>();
    const [sortOptions, setSortOptions] = useState<SortOption>({
      field: 'date_created',
      order: 'desc',
      title: '',
    });
    const libraryRepo = useRef(new LibraryRepository());
    const isLoadingRef = useRef(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const loadPage = useCallback(
      async (
        pageToLoad: number,
        filter: number,
        query: string,
        sort: SortOption,
      ) => {
        if (isLoadingRef.current) return;
        isLoadingRef.current = true;
        setIsLoading(true);
        const typeFilter = filter === FILTER_ALL ? undefined : filter;
        const result = await libraryRepo.current.getMediaFiles(
          pageToLoad,
          organizationId,
          PAGE_SIZE,
          sort.field,
          typeFilter,
          query,
          sort.order === 'asc',
        );
        if (result.success && result.data) {
          const raw = result.data.data;
          const filtered =
            filter === FILTER_ALL
              ? raw.filter(f => {
                  const t = (f.fileType || '').toLowerCase();
                  return t.includes('image') || t.includes('video');
                })
              : raw;
          setFiles(prev =>
            pageToLoad === 0 ? filtered : [...prev, ...filtered],
          );
          const totalPages = result.data.pagesCount ?? 0;
          setHasMore(pageToLoad + 1 < totalPages);
          setPage(pageToLoad + 1);
        } else {
          setHasMore(false);
        }
        setIsLoading(false);
        isLoadingRef.current = false;
      },
      [organizationId],
    );

    useEffect(() => {
      setFiles([]);
      setPage(0);
      setHasMore(true);
      isLoadingRef.current = false;
      loadPage(0, activeFilter, search, sortOptions);
    }, [search, activeFilter, sortOptions, loadPage]);

    useEffect(() => {
      if (tmpSearch === '' && search !== '') setSearch('');
    }, [tmpSearch, search]);

    useEffect(() => {
      if (!hasMore || isLoading) return;
      const observer = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting) {
            loadPage(page, activeFilter, search, sortOptions);
          }
        },
        {root: scrollRef.current, rootMargin: '300px', threshold: 0},
      );
      const el = bottomRef.current;
      if (el) observer.observe(el);
      return () => {
        if (el) observer.unobserve(el);
      };
    }, [isLoading, hasMore, page, activeFilter, search, sortOptions, loadPage]);

    const onSelectCard = (file: MediaFile) => {
      if (!isFilePickable(file)) return;
      const result = fileToResult(file);
      const same =
        pending?.libraryMediaFileId === result.libraryMediaFileId &&
        pending?.source === 'library';
      onPick(same ? null : result);
    };

    const onUploadFile = (file: File) => {
      const t = (file.type || '').toLowerCase();
      if (!t.includes('image') && !t.includes('video')) return;
      setUploadedFile(file);
    };

    const onCloseUpload = () => {
      setUploadedFile(undefined);
      setFiles([]);
      setPage(0);
      setHasMore(true);
      isLoadingRef.current = false;
      loadPage(0, activeFilter, search, sortOptions);
    };

    return (
      <>
        <div className={styles.searchRow}>
          <div className={styles.searchWrapper}>
            <SearchInput2
              value={tmpSearch}
              onSearch={setSearch}
              onTyping={setTmpSearch}
              disabled={disabled}
            />
          </div>
          <div className={styles.uploadSlot}>
            <UploadVideoButton
              onVideoSelected={onUploadFile}
              type={MEDIA_FILES_ALL_VIDEO}
              acceptOverride="image/jpg,image/jpeg,image/png,video/mp4,video/x-m4v"
            />
          </div>
        </div>
        <div className={styles.filtersRow}>
          <div className={styles.filters}>
            {FILTER_IDS.map(id => {
              let opts;
              if (id === FILTER_ALL) {
                opts = {
                  icon: '',
                  id: FILTER_ALL,
                  title: VideoEditorText.editMediaFilterAll,
                };
              } else if (id === MEDIA_FILES_AI_VIDEO) {
                const base = getMediaFilterOptions(id);
                opts = {...base, title: VideoEditorText.editMediaFilterAiVideo};
              } else {
                opts = getMediaFilterOptions(id);
              }
              return (
                <FilterPill
                  key={opts.id}
                  id={opts.id}
                  icon={opts.icon}
                  title={opts.title}
                  selected={activeFilter === opts.id}
                  onSelect={setActiveFilter}
                />
              );
            })}
          </div>
          <div className={styles.sortSlot}>
            <SortPill
              sortField={sortOptions.field}
              sortDirection={sortOptions.order}
              options={LIBRARY_SORT_OPTIONS}
              onSetSort={(field, order) => setSortOptions({field, order})}
            />
          </div>
        </div>

        <div className={styles.content} ref={scrollRef}>
          {isLoading && files.length === 0 && (
            <div className={`${styles.loading} ${styles.loadingFull}`}>
              <LoadingSpinner />
            </div>
          )}
          {!isLoading && files.length === 0 && (
            <p className={styles.empty}>{appText.noMoreItems}</p>
          )}
          <div className={styles.grid}>
            {files.map(file => {
              const isSelected =
                pending?.source === 'library' &&
                pending?.libraryMediaFileId === file.id;
              const isImage = isImageFile(file.fileType);
              const thumb = isImage
                ? file.fileUrl || file.placeholderUrl || ''
                : file.placeholderUrl || file.fileUrl || '';
              return (
                <EditMediaItemCard
                  key={file.id}
                  thumbnailUrl={thumb}
                  title={file.fileName}
                  duration={file.duration || 0}
                  selected={isSelected}
                  disabled={disabled || !isFilePickable(file)}
                  onClick={() => onSelectCard(file)}
                />
              );
            })}
            {files.length > 0 && hasMore && (
              <div ref={bottomRef} className={styles.loadMoreTrigger} />
            )}
          </div>
          {isLoading && files.length > 0 && (
            <div className={styles.loading}>
              <LoadingSpinner />
            </div>
          )}
        </div>

        {uploadedFile && (
          <FileUploadModalBody
            file={uploadedFile}
            onClose={onCloseUpload}
            onSuccess={onCloseUpload}
          />
        )}
      </>
    );
  },
);

export default EditMediaLibrarySource;
